// Special mission detail view: clipboard paste, photo preview, countdown timer.

import { uploadViaPresigned } from '../upload-helper.js';

document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('.s-mission');
    if (!root) return;

    initPhotoPreview(root);
    initPhotoRemove(root);
    initPaste(root);
    initSingleAnswerGuard(root);
    initPresignedSubmit(root);
    initTimer(root);
});

function initPresignedSubmit(root) {
    const form = root.querySelector('.s-mission__form');
    if (!form) return;

    const submitBtn = form.querySelector('.s-mission__btn-submit');
    let submitting = false;

    form.addEventListener('submit', async (e) => {
        if (submitting) return; // already in presigned flow, let the real submit go

        const fileInputs = Array.from(form.querySelectorAll('input[type="file"][data-slot]'));
        const withFiles = fileInputs.filter((i) => i.files && i.files[0]);
        if (withFiles.length === 0) return; // text/link submission — normal path

        e.preventDefault();
        submitting = true;

        const originalText = submitBtn ? submitBtn.textContent : '';
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'WYSYŁANIE...';
        }

        for (const input of withFiles) {
            const file = input.files[0];
            const slot = input.dataset.slot;

            const key = await uploadViaPresigned(file, 'special-mission');

            if (key) {
                // Presigned succeeded: add hidden key field, disable the file input so multipart doesn't carry the bytes again
                const hidden = document.createElement('input');
                hidden.type = 'hidden';
                hidden.name = `answer_${slot}_key`;
                hidden.value = key;
                form.appendChild(hidden);
                input.disabled = true;
            }
            // else: fallback — input stays enabled, will be sent traditionally via multipart
        }

        // Re-enable submit button so the dispatched submit() doesn't get blocked
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }

        form.submit();
    });
}

function initPhotoPreview(root) {
    root.querySelectorAll('input[type="file"][data-slot]').forEach((input) => {
        input.addEventListener('change', () => {
            const file = input.files && input.files[0];
            if (!file) return;

            const challenge = input.closest('.s-mission__challenge');
            if (!challenge) return;

            const placeholder = challenge.querySelector('.s-mission__photo-placeholder');
            const preview = challenge.querySelector('.s-mission__photo-preview');
            const videoAdded = challenge.querySelector('.s-mission__video-added');

            const isVideo = file.type.startsWith('video/');

            if (isVideo) {
                // Wideo bywa duze (do 25 MB) - nie czytamy data URL, pokazujemy tylko nazwe.
                if (videoAdded) {
                    const nameEl = videoAdded.querySelector('.s-mission__video-added-name');
                    if (nameEl) nameEl.textContent = file.name;
                    videoAdded.hidden = false;
                }
                if (preview) preview.hidden = true;
                if (placeholder) placeholder.hidden = true;
                return;
            }

            if (!preview) return;
            const img = preview.querySelector('img');

            // data: URL (nie blob:) - produkcyjne CSP img-src bywa bez 'blob:' a z 'data:'.
            const reader = new FileReader();
            reader.onload = () => {
                img.src = reader.result;
                preview.hidden = false;
                if (videoAdded) videoAdded.hidden = true;
                if (placeholder) placeholder.hidden = true;
            };
            reader.readAsDataURL(file);
        });
    });
}

function initPhotoRemove(root) {
    root.querySelectorAll('.s-mission__photo-remove').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const challenge = btn.closest('.s-mission__challenge');
            if (!challenge) return;

            const fileInput = challenge.querySelector('input[type="file"][data-slot]');
            if (fileInput) fileInput.value = '';

            const preview = challenge.querySelector('.s-mission__photo-preview');
            if (preview) {
                preview.hidden = true;
                const img = preview.querySelector('img');
                if (img) img.src = '';
            }

            const placeholder = challenge.querySelector('.s-mission__photo-placeholder');
            if (placeholder) placeholder.hidden = false;

            const videoAdded = challenge.querySelector('.s-mission__video-added');
            if (videoAdded) {
                videoAdded.hidden = true;
                const nameEl = videoAdded.querySelector('.s-mission__video-added-name');
                if (nameEl) nameEl.textContent = '';
            }
        });
    });
}

function initPaste(root) {
    root.querySelectorAll('.s-mission__paste-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const slot = btn.dataset.slot;
            const input = root.querySelector(`input[type="url"][data-slot="${slot}"]`);
            if (!input) return;

            try {
                const text = await navigator.clipboard.readText();
                if (text) {
                    input.value = text;
                    input.focus();
                }
            } catch {
                input.focus();
            }
        });
    });
}

// Multi-challenge missions: user must pick exactly ONE option.
// On submit, count filled slots; if more than one is filled, block the submission
// and show an inline error. Server-side validation is the source of truth, but
// this prevents a wasted round-trip and gives immediate feedback.
function initSingleAnswerGuard(root) {
    const form = root.querySelector('.s-mission__form');
    if (!form) return;

    const challenges = form.querySelectorAll('.s-mission__challenge');
    if (challenges.length < 2) return;

    const isFilled = (challenge) => {
        const file = challenge.querySelector('input[type="file"][data-slot]');
        if (file && file.files && file.files.length > 0) return true;
        const text = challenge.querySelector('input[type="url"][data-slot], input[type="text"][data-slot], textarea[data-slot]');
        if (text && text.value.trim() !== '') return true;
        return false;
    };

    const errorEl = document.createElement('p');
    errorEl.className = 's-mission__multi-error';
    errorEl.hidden = true;
    errorEl.textContent = 'Możesz wypełnić tylko jedną opcję – usuń odpowiedź z pozostałych.';
    const submitBtn = form.querySelector('.s-mission__btn-submit');
    if (submitBtn) submitBtn.parentNode.insertBefore(errorEl, submitBtn);

    form.addEventListener('submit', (e) => {
        const filledCount = Array.from(challenges).filter(isFilled).length;
        if (filledCount > 1) {
            e.preventDefault();
            errorEl.hidden = false;
            errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            errorEl.hidden = true;
        }
    });
}

function initTimer(root) {
    const form = root.querySelector('[data-deadline]');
    const out = root.querySelector('[data-timer]');
    if (!form || !out) return;

    const deadlineRaw = form.dataset.deadline;
    if (!deadlineRaw) return;

    const deadline = new Date(deadlineRaw).getTime();
    if (Number.isNaN(deadline)) return;

    const tick = () => {
        const diffMs = deadline - Date.now();
        if (diffMs <= 0) {
            out.textContent = '00:00:00';
            return false;
        }
        const totalSec = Math.floor(diffMs / 1000);
        const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
        const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
        const s = String(totalSec % 60).padStart(2, '0');
        out.textContent = `${h}:${m}:${s}`;
        return true;
    };

    if (tick()) {
        setInterval(() => {
            if (!tick()) {
                clearInterval(this);
            }
        }, 1000);
    }
}
